import calculateGetItBy from '@carvana/merch-tile/utils/availability/calculateGetItBy';
import calculateSoonestAvailable from '@carvana/merch-tile/utils/availability/calculateSoonestAvailable';
import cx from 'classnames';
import { useContext } from 'react';
import CtaButton from '@/vdp/CtaButton';
import Price from '@/vdp/PriceExperiment';
import Tags from '@/vdp/Tags';
import TransitChargeState from '~/constants/vdp/TransitChargeState';
import DeliveryPickupContext, {
  useDeliveryInfo,
  usePickupInfo
} from '~/context/vdp/DeliveryPickupContext';
import GlobalContext from '~/context/vdp/GlobalContext';
import { useTransitChargeInfo } from '~/context/vdp/UserVehicleContext';
import { useGetVehicleDetails } from '~/context/vdp/VehicleContext';
import useCtaButtonState from '~/hooks/vdp/selectors/useCtaButtonState';
import useIsNotAvailableForImmediatePurchase from '~/hooks/vdp/selectors/useIsNotAvailableForImmediatePurchase';
import useIsInHeroRefreshTreatment from '~/hooks/vdp/useIsInHeroRefreshTreatment';
import Star from '~/icons/Star';
import formatAndDropDecimal from '~/utilities/formatAndDropDecimal';

function Skeleton({ width }: { width: number }) {
  return (
    <div className="h-24 py-4" style={{ width }}>
      <div className="h-full bg-grey-1" />
    </div>
  );
}

type OwnProps = {
  isVDPLite?: boolean;
};

export default function HeroVehicleInfo({ isVDPLite = false }: OwnProps) {
  const { vehicleRating } = useContext(GlobalContext);
  const { deliveryPickup } = useContext(DeliveryPickupContext);
  const { make, mileage = 0, model, trim, year } = useGetVehicleDetails();
  const { isDisabled, notifyMe } = useCtaButtonState();
  const delivery = useDeliveryInfo();
  const pickup = usePickupInfo();
  const transitCharge = useTransitChargeInfo();
  const isInHeroRefreshTreatment = useIsInHeroRefreshTreatment();
  const isAvailableForImmediatePurchase =
    !useIsNotAvailableForImmediatePurchase();

  const soonestAvailable = calculateSoonestAvailable({ delivery, pickup });
  const { getItBy } = calculateGetItBy(soonestAvailable, false, true);

  const isDeliveryPickupLoading =
    isAvailableForImmediatePurchase && !deliveryPickup.isLoaded;
  const isTransitChargeLoading =
    isAvailableForImmediatePurchase && !transitCharge.isLoaded;

  const hasTransitCharge =
    transitCharge.state !== TransitChargeState.Unknown ||
    isTransitChargeLoading;
  const hasGetItBy = Boolean(getItBy) || isDeliveryPickupLoading;

  return (
    <div className="grid h-fit gap-y-16 p-16 m:gap-y-2xl m:p-2xl l:gap-y-3xl l:p-0">
      <div className="grid gap-y-lg m:gap-y-16">
        <Tags />

        <div className="text-blue-6">
          <p className="tl-header-m m:tl-header-xl">
            {year} {make} {model}
          </p>
          <p className="grid w-fit grid-flow-col gap-x-4 tl-body-s m:gap-x-md m:tl-body-m">
            <span>{trim}</span>
            <span>•</span>
            <span>{mileage.toLocaleString()} miles</span>
          </p>
        </div>

        {isInHeroRefreshTreatment ? (
          !vehicleRating.isLoaded ? (
            <Skeleton width={140} />
          ) : vehicleRating.data.averageRating ? (
            <div className="flex h-24 items-center text-blue-6">
              <Star className="fill-yellow-2" />
              <span className="ml-[2px] mr-[6px] font-semibold">
                {vehicleRating.data.averageRating.toLocaleString(undefined, {
                  minimumFractionDigits: 1
                })}
              </span>
              <a
                href="#owner-reviews"
                className="underline"
                data-analytics-id="Hero"
                data-analytics-attributes={JSON.stringify({
                  description: 'Owner Reviews'
                })}
              >
                {vehicleRating.data.totalRatingCounts.toLocaleString()} reviews
              </a>
            </div>
          ) : null
        ) : null}
      </div>

      <div className="grid gap-y-md">
        <Price />

        {hasTransitCharge || hasGetItBy ? (
          <div className="m:tl-body-m">
            {isTransitChargeLoading ? (
              <Skeleton width={90} />
            ) : hasTransitCharge ? (
              <p
                className={cx('-:text-grey-5', {
                  'font-bold text-green-2':
                    transitCharge.state !== TransitChargeState.HasCharge
                })}
              >
                {transitCharge.state === TransitChargeState.HasCharge
                  ? `$${formatAndDropDecimal(transitCharge.charge)}`
                  : 'Free'}{' '}
                shipping
              </p>
            ) : null}

            {isDeliveryPickupLoading ? (
              <Skeleton width={105} />
            ) : hasGetItBy ? (
              <p
                className={cx('text-blue-6', {
                  'font-bold': Number(soonestAvailable.daysToAvailability) < 4
                })}
              >
                Get it {getItBy}
              </p>
            ) : null}
          </div>
        ) : null}
      </div>

      {isVDPLite && notifyMe ? null : (
        <div className="hidden l:grid l:gap-y-md">
          <CtaButton
            className={cx('w-full', { disabled: isDisabled })}
            data-analytics-id="Hero - CTA Button"
            data-qa="get-started-button"
          />
        </div>
      )}
    </div>
  );
}
